import { STORE_KEY } from "../constants";
import { persistence, PERSISTENCE_APP_KEY_FULL } from "./persistence";
import { isSupabaseConfigured, supabase } from "./supabase";
import { logger, toLogError } from "../utils/logger";

function getStorage() {
  if (window.storage && typeof window.storage.get === "function") {
    return {
      async get(key) {
        const r = await window.storage.get(key);
        return r ? r.value : null;
      },
      async set(key, value) {
        await window.storage.set(key, value);
      },
    };
  }
  return null;
}

/**
 * Applique coaches.settings (JSONB) par-dessus le payload app_data — la colonne settings prime pour ces clés.
 */
export function mergeSettingsJsonIntoPayload(settings, payload) {
  if (!settings || typeof settings !== "object") return payload;
  const base = payload && typeof payload === "object" ? payload : {};
  const next = { ...base };
  if (settings.caObjectif != null) next.caObjectif = settings.caObjectif;
  if (settings.revenueGoal != null && typeof settings.revenueGoal === "object") next.revenueGoal = settings.revenueGoal;
  if (settings.defaultSessionPrice != null) next.sessionTarif = settings.defaultSessionPrice;
  if (settings.isSAP != null) next.isSAP = !!settings.isSAP;
  if (settings.devise != null) next.devise = settings.devise;
  if (settings.lastPaymentMethod != null) next.lastPaymentMethod = settings.lastPaymentMethod;
  return next;
}

function buildSettingsMergeFromPersistPayload(d) {
  return {
    caObjectif: d.caObjectif,
    revenueGoal: d.revenueGoal,
    defaultSessionPrice: d.sessionTarif,
    isSAP: d.isSAP,
    devise: d.devise,
    lastPaymentMethod: d.lastPaymentMethod,
  };
}

/**
 * Charge app_data + settings pour un coach.
 */
export async function loadRemoteCoachState(coachId) {
  if (!supabase || !coachId) return null;
  try {
    const { data, error } = await supabase.from("coaches").select("app_data, settings").eq("id", coachId).single();
    if (error) {
      logger.error("Persistence loadRemoteCoachState", toLogError(error));
      return null;
    }
    const raw = data?.app_data;
    const appData =
      raw == null || (typeof raw === "object" && Object.keys(raw).length === 0)
        ? null
        : typeof raw === "object"
          ? raw
          : null;
    const settings = data?.settings && typeof data.settings === "object" ? data.settings : {};
    return { appData, settings };
  } catch (e) {
    logger.error("Persistence loadRemoteCoachState (catch)", toLogError(e));
    return null;
  }
}

/**
 * @deprecated Utiliser loadRemoteCoachState — conservé pour imports existants
 */
export async function loadRemoteAppState(coachId) {
  const bundle = await loadRemoteCoachState(coachId);
  return bundle?.appData ?? null;
}

/**
 * Sauvegarde app_data + fusion coaches.settings (JSONB) pour les clés métier.
 */
export async function saveRemoteCoachState(coachId, d) {
  if (!supabase || !coachId || coachId === "local-coach") return;
  try {
    let mergedSettings = buildSettingsMergeFromPersistPayload(d);
    const { data: row, error: readErr } = await supabase.from("coaches").select("settings").eq("id", coachId).single();
    if (readErr) {
      logger.error("Persistence saveRemoteCoachState read", toLogError(readErr));
    } else {
      const prev = row?.settings && typeof row.settings === "object" ? row.settings : {};
      mergedSettings = { ...prev, ...mergedSettings };
    }
    const { error } = await supabase
      .from("coaches")
      .update({ app_data: d, settings: mergedSettings })
      .eq("id", coachId);
    if (error) {
      logger.error("Persistence saveRemoteCoachState update", toLogError(error));
    }
  } catch (e) {
    logger.error("Persistence saveRemoteCoachState (catch)", toLogError(e));
  }
}

/** @deprecated */
export async function saveRemoteAppState(coachId, d) {
  return saveRemoteCoachState(coachId, d);
}

/**
 * Charge l’état persisté (Tauri async ou navigateur via persistence).
 */
export async function loadData() {
  try {
    const adapter = getStorage();
    if (adapter) {
      let raw = await adapter.get(PERSISTENCE_APP_KEY_FULL);
      if (raw == null) raw = await adapter.get(STORE_KEY);
      if (raw == null) return null;
      return typeof raw === "string" ? JSON.parse(raw) : raw;
    }
    return persistence.getAppState();
  } catch {
    return null;
  }
}

/**
 * Sauvegarde l’état (même format JSON qu’avant).
 * @param {object} d — payload sérialisable
 * @param {string} [coachId] — UUID Supabase ; sinon déduit du mode local uniquement
 */
export async function saveData(d, coachId) {
  try {
    if (isSupabaseConfigured() && coachId && coachId !== "local-coach") {
      await saveRemoteCoachState(coachId, d);
      return;
    }
    const adapter = getStorage();
    const payload = JSON.stringify(d);
    if (adapter) {
      await adapter.set(PERSISTENCE_APP_KEY_FULL, payload);
      return;
    }
    persistence.setAppState(d);
  } catch (e) {
    logger.error("Persistence saveData", toLogError(e));
  }
}
